import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';

const Hero = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true });
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  const lineVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.15 + 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    })
  };

  const textLines = [
    "REDEFINING",
    "THE FUTURE",
    "OF SECURITY."
  ];

  return (
    <section ref={containerRef} className="relative min-h-screen flex flex-col justify-center pt-32 pb-20">
      <motion.div style={{ y, opacity }} className="absolute inset-0 z-0">
        <video 
          src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/cwvk2jks_Website%20video%20v3.mp4" 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover opacity-70 mix-blend-luminosity scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-[#050505]/20 to-[#050505]"></div>
      </motion.div>

      <div className="container mx-auto px-6 relative z-10 flex flex-col justify-center h-full">
        <div className="max-w-5xl">
          <div className="flex flex-col gap-2 flex-shrink-0 mt-4">
            {textLines.map((line, i) => (
              <div key={i} className={`overflow-visible ${i === 2 ? 'pb-8' : ''}`}>
                <h1 className="text-6xl md:text-8xl lg:text-[8.5rem] font-space-grotesk font-bold uppercase tracking-tighter leading-[0.95]">
                  {i === 2 ? (
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#FF9000] drop-shadow-[0_0_15px_rgba(255,215,0,0.3)] inline-block pb-4">
                      {line}
                    </span>
                  ) : line}
                </h1>
              </div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 max-w-xl"
          >
            <p className="text-gray-400 text-lg md:text-xl font-light leading-relaxed">
              Comprehensive managed solutions to reduce risk and provide increasing business value through continuous improvement and innovation.
            </p>
            <button className="mt-8 group relative px-10 py-5 bg-transparent overflow-hidden rounded-sm">
              <div className="absolute inset-0 bg-[#FFD700] transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"></div>
              <span className="relative z-10 font-space-grotesk uppercase tracking-wider text-sm font-bold transition-colors duration-500 text-white group-hover:text-black">
                Discover Our Approach
              </span>
              <div className="absolute inset-0 border border-[#FFD700]/30 transition-colors duration-500 group-hover:border-transparent rounded-sm"></div>
            </button>
          </motion.div>
        </div>
      </div>
      
      {/* Slow Marquee at the bottom of hero */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden bg-black/50 backdrop-blur-md border-t border-white/5 py-4">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="flex gap-16 items-center px-8 text-sm font-space-grotesk uppercase tracking-widest text-gray-500">
              <span>Predict</span>
              <span className="w-2 h-2 rounded-full bg-[#FFD700]/50"></span>
              <span>Prevent</span>
              <span className="w-2 h-2 rounded-full bg-[#FFD700]/50"></span>
              <span>Respond</span>
              <span className="w-2 h-2 rounded-full bg-[#FFD700]/50"></span>
              <span>Manage</span>
              <span className="w-2 h-2 rounded-full bg-[#FFD700]/50"></span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
